import React, { useState } from "react";
import {
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
} from "@mui/material";
import Text from "src/components/Displays/Text";
import { ChevronDown, QuestionMark, Edit } from "src/assets";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useTranslation } from "react-i18next";
import {
  SESSION_KEYS,
  SITE_TECHNICAL_DATA,
  USER_PROFILE_DATA,
  BONUS,
} from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { WallboxTypes } from "src/pages/Wallbox";
import { SmartMeterTypes } from "src/pages/SmartMeter";
import { Tariff } from "src/models/clientsModel";
import CheckWhite from "src/assets/icons/checkWhite.svg";
import Check from "src/assets/icons/check.svg";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/consts/routes";
import { BatterySetupTypes } from "src/pages/BatterySetup";
import { PvSetupTypes } from "src/pages/PvSetup";
import { ThemeValues } from "src/components/ThemeUsage/theme";
import { ThemeContext } from "src/components/ThemeUsage/ThemeUsage";
import { useContext } from "react";
import { dateFormat } from "src/helpers/dateFormat";

interface CalculatedSavingsSummaryProps {
  showSavingsResult?: boolean;
  themeName?: string;
  tariffsData?: Tariff[];
  getLogo: () => { houseImg: string; cssProps: any };
  toggleIsOverlayOpenFlex?: () => void;
}

const CalculatedSavingsSummary: React.FC<CalculatedSavingsSummaryProps> = ({
  tariffsData,
  showSavingsResult = false,
  getLogo,
  toggleIsOverlayOpenFlex,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const campaignBonusDisplayName = getBonus()?.logicalId ?? "";
  const campaignBonusValidTill = getBonus()?.endDate ?? "";
  const showCampaignText = campaignBonusDisplayName.length > 0;
  const showBonusValidTillText = campaignBonusValidTill.length > 0;
  const bonusValidTillDate =
    showBonusValidTillText && dateFormat(campaignBonusValidTill);

  const [getSessionStorageItem] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const [getUserSessionStorage] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );

  const savingsData = tariffsData && tariffsData[0];

  const flexTariffDisplayName =
    (savingsData && savingsData.flexTariff?.tariffDisplayName) ?? "";

  const savingsYearly = savingsData && savingsData.savings?.savingsYearly;

  const flexWorkingPricePerKwh =
    (savingsData && savingsData.flexTariff?.workingPricePerKwh) ?? "";
  const flexSupplyPriceFixedElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFixedElementPerKwh) ??
    "";
  const flexSupplyPriceFlexElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFlexElementPerKwh) ?? "";

  const estimatedYearlyCostsRaw = savingsData?.flexTariff?.estimatedYearlyCosts;

  const estimatedYearlyCostsNumber: number | null =
    estimatedYearlyCostsRaw != null
      ? Number(String(estimatedYearlyCostsRaw).replace(",", "."))
      : null;

  const estimatedMonthlyCosts: string =
    estimatedYearlyCostsNumber !== null
      ? (estimatedYearlyCostsNumber / 12).toFixed(2).replace(".", ",")
      : "-- ; --";

  const wallbox =
    getSessionStorageItem()?.wallboxInstalled === WallboxTypes.JA
      ? t("page.wallbox.yes")
      : t("page.wallbox.no");

  const smartMeter =
    getSessionStorageItem()?.smartMeterInstalled === SmartMeterTypes.JA
      ? t("page.smartMeter.yes")
      : t("page.smartMeter.no");

  const batteryCapacity =
    getSessionStorageItem()?.batterySetup?.batteryCapacity ?? 0;

  const handleValueChangeClick = () => {
    setBonus(null);
    setTariffs([]);
    navigate(ROUTES.PV_SETUP);
  };

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const batterySetup =
    getSessionStorageItem()?.batterySetup?.batteryInstalled ===
    BatterySetupTypes.JA
      ? `${batteryCapacity} kWh`
      : t("page.batterySetup.no");

  const pvSetup =
    getSessionStorageItem()?.pvSetup?.pvInstalled === PvSetupTypes.JA
      ? `${getSessionStorageItem()?.pvSetup?.pvPowerMaxKwp} kWp`
      : t("page.batterySetup.no");

  const themeName = useContext(ThemeContext);

  const isLichtblick = themeName === ThemeValues.lichtblick;

  return (
    <Stack sx={{ width: "100%" }}>
      <Accordion
        elevation={0}
        disableGutters
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
        sx={{ width: "100%", bgcolor: "transparent" }}
      >
        <AccordionSummary
          sx={(theme) => ({
            paddingTop: { xs: "10px", md: "30px" },
            paddingX: { xs: "10px", md: "24px" },
            paddingBottom: "5px",
            bgcolor: isLichtblick ? theme.palette.app.secondary : "#DAECFA",
            width: "100%",
            boxSizing: "border-box",
            "& .MuiAccordionSummary-content": {
              margin: 0,
              width: "100%",
            },
            borderRadius: isExpanded ? "12px 12px 0 0" : "12px",
          })}
        >
          <Stack gap={0} sx={{ width: "100%" }}>
            {showSavingsResult && (
              <Stack
                direction="column"
                gap={1.5}
                sx={{
                  mb: 3,
                  bgcolor: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #039C25",
                  overflow: "hidden",
                  width: "100%",
                  p: { xs: 2, sm: 2.5 },
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Text
                    sx={{
                      color: "#222222",
                      fontSize: { xs: "11px", sm: "16px" },
                      fontWeight: 400,
                    }}
                    text={t("page.calculatedSavingsSummary.potentialSavings")}
                  />
                  <Stack direction="row">
                    <Stack gap={0.5} direction={"row"} alignItems={"center"}>
                      <Text
                        sx={{
                          color: "#31825E",
                          fontSize: { xs: "11px", sm: "16px" },
                          fontWeight: 900,
                        }}
                        text={
                          <>
                            {`${savingsYearly} ${t(
                              "page.calculatedSavingsSummary.year",
                            )}`}
                          </>
                        }
                      />
                      <Text
                        sx={{
                          color: "#009A22",
                          marginTop: -3,
                          marginLeft: -0.5,
                          fontSize: { xs: "8px", sm: "10px" },
                          fontFamily: "isonType",
                          fontWeight: 700,
                        }}
                        text={`1`}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                    borderRadius: 1,
                    bgcolor: "#C6FFB2",
                    border: "none",
                    py: 1,
                    px: 2,
                    boxSizing: "border-box",
                  }}
                >
                  <Text
                    text={t(
                      "page.calculatedSavings.potentialSavingsComparement",
                    )}
                    sx={{
                      color: "#367056",
                      fontSize: { xs: "10px", sm: "13px" },
                      fontWeight: 600,
                      lineHeight: 1.3,
                      textAlign: "center",
                    }}
                  />
                </Stack>
              </Stack>
            )}
            {showCampaignText && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  bgcolor: "#31825E",
                  py: 1.5,
                  px: 2,
                  mb: 3,
                  boxSizing: "border-box",
                }}
              >
                {showBonusValidTillText && (
                  <Text
                    text={`${t(
                      `page.tariff.timeline.onlyTill`,
                    )} ${bonusValidTillDate}`}
                    sx={{
                      color: "#E9FFE1",
                      fontSize: "11px",
                      fontWeight: 400,
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  />
                )}

                <Text
                  text={
                    <>
                      {t(
                        `page.tariff.timeline.bonusSavings.${campaignBonusDisplayName}` as any,
                      )}
                      <sup style={{ fontSize: "0.4em" }}>4</sup>
                    </>
                  }
                  sx={{
                    color: "#E9FFE1",
                    fontSize: "18px",
                    fontWeight: 700,
                    lineHeight: 1.3,
                    textAlign: "center",
                  }}
                />
              </Stack>
            )}
            <Stack
              gap={0}
              sx={{
                width: "100%",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
            >
              <Stack
                sx={{
                  backgroundColor: isLichtblick ? "#BFCEDE" : "#084465",
                  borderRadius: "8px",
                  width: "100%",
                  p: { xs: 1, sm: 1.5 },
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    backgroundColor: isLichtblick ? "#30475C" : "#105A83",
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                    borderRadius: 2,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Stack
                    direction="column"
                    alignItems="flex-start"
                    gap={{ xs: 1, sm: 2 }}
                  >
                    <Text
                      sx={{
                        color: isLichtblick ? "#F7BCCE" : "#FFFFFF",
                        fontSize: { xs: "12px", sm: "12px" },
                        fontWeight: 700,
                        lineHeight: "15px",
                      }}
                      text={flexTariffDisplayName}
                    />
                  </Stack>

                  <Stack
                    direction="column"
                    alignItems="flex-end"
                    sx={{ color: "#FFFFFF" }}
                  >
                    <Stack direction="row">
                      <Stack gap={0} direction="row" alignItems="center">
                        <Text
                          sx={{
                            color: isLichtblick ? "#F7BCCE" : "#FFFFFF",
                            fontSize: { xs: "15px", sm: "16px" },
                            fontWeight: 900,
                          }}
                          text={<>{`${estimatedMonthlyCosts} €/mtl.`}</>}
                        />
                        <Text
                          sx={{
                            color: isLichtblick ? "#F7BCCE" : "#FFFFFF",
                            marginTop: -1,
                            fontSize: { xs: "10px", sm: "10px" },
                            fontFamily: "isonType",
                            fontWeight: 700,
                          }}
                          text={`1`}
                        />
                      </Stack>
                    </Stack>

                    <Text
                      sx={{
                        color: "#FFFFFF",
                        fontSize: { xs: "8px", sm: "8px" },
                        mt: -0.5,
                      }}
                      text={t(
                        "page.tariff.tariffSelectionContainer.calculatedCosts",
                      )}
                    />
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  sx={{
                    py: 1.5,
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    backgroundColor: isLichtblick ? "#BFCEDE" : "#084465",
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{ padding: 0, px: { xs: 1, sm: 2 } }}
                    width="100%"
                    justifyContent="center"
                    gap={{ xs: 2, sm: 4 }}
                  >
                    {[
                      t("page.calculatedSavingsSummary.checkbox.point1"),
                      t("page.calculatedSavingsSummary.checkbox.point3"),
                    ].map((feature, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 1, sm: 1.5 }}
                      >
                        <Box
                          component="img"
                          src={(isLichtblick ? Check : CheckWhite) as string}
                          sx={{
                            width: 10,
                            height: 10,
                            flexShrink: 0,
                          }}
                        />
                        <Text
                          text={feature}
                          sx={{
                            fontSize: { xs: "9px", sm: "10px" },
                            color: isLichtblick ? "#000000" : "#FFFFFF",
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Stack>

              {!isExpanded && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => setIsExpanded(true)}
                  sx={{ cursor: "pointer" }}
                >
                  <Text
                    sx={{
                      color: "#000",
                      fontSize: "11px",
                      fontWeight: 400,
                      textDecoration: "underline",
                      marginY: "10px",
                    }}
                    text={t("page.calculatedSavings.calculationDetails")}
                  />
                  <ChevronDown />
                </Stack>
              )}
            </Stack>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, width: "100%" }}>
          <Stack
            sx={(theme) => ({
              px: { xs: "10px", md: "24px" },
              py: 2,
              gap: 2,
              bgcolor: isLichtblick ? theme.palette.app.secondary : "#DAECFA",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
              boxSizing: "border-box",
              width: "100%",
            })}
          >
            <Text
              sx={{ color: "#222", fontSize: "12px", fontWeight: 700 }}
              text={t("page.calculatedSavingsSummary.title1")}
            />
            <Divider sx={{ my: 0, mb: "2px" }} />
            <Text
              sx={{
                color: "#222",
                fontSize: "11px",
                fontWeight: 400,
                lineHeight: 1.5,
              }}
              text={t("page.calculatedSavingsSummary.description")}
            />
            {toggleIsOverlayOpenFlex && (
              <QuestionMark
                onClick={toggleIsOverlayOpenFlex}
                style={{ marginLeft: 8, cursor: "pointer" }}
              />
            )}
            <Stack gap={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={0}
                alignItems="center"
              >
                <Text
                  sx={{
                    color: "#222",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                  text={t("page.calculatedSavingsSummary.title2")}
                />
                <PrimaryButton
                  onClick={handleValueChangeClick}
                  sx={() => ({
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#000000",
                    bgcolor: "#ffffff00",
                    maxHeight: "20px",
                    border: "1px solid #707070",
                    paddingX: "10px",
                    "&:hover": {
                      backgroundColor: "#ffffff00",
                    },
                    "& .MuiButton-endIcon": {
                      marginLeft: "6px",
                    },
                  })}
                  text={t("page.calculatedSavingsSummary.changeValues")}
                  endIcon={
                    <Edit
                      style={{
                        height: "12px",
                        marginLeft: "-2.5px",
                      }}
                    />
                  }
                />
              </Stack>

              <Divider sx={{ my: "4px", mt: "-2px" }} />

              <Stack gap={0}>
                {[
                  {
                    label: t(
                      "page.calculatedSavingsSummary.annualConsumptionLable",
                    ),
                    value: `${getUserSessionStorage()?.yearlyGridSupplyKwh} kWh/Jahr`,
                  },
                  {
                    label: t("page.address.formFields.zipCode.label"),
                    value: `${getSessionStorageItem()?.pvSetup?.postalCode}`,
                  },
                  {
                    label: t("page.calculatedSavingsSummary.pvLabel"),
                    value: `${pvSetup}`,
                  },
                  {
                    label: t("page.calculatedSavingsSummary.wallboxInstalled"),
                    value: `${wallbox}`,
                  },
                  {
                    label: t("page.calculatedSavingsSummary.homeStorage"),
                    value: `${batterySetup}`,
                  },
                  {
                    label: t("page.calculatedSavingsSummary.smartMeter"),
                    value: `${smartMeter}`,
                  },
                ].map((item, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Text
                      sx={{
                        color: "#222",
                        fontSize: "11px",
                        fontWeight: 400,
                        lineHeight: 1.2,
                      }}
                      text={`${item.label}:`}
                    />
                    <Text
                      sx={{
                        color: "#222",
                        fontSize: "11px",
                        fontWeight: 400,
                        lineHeight: 2,
                      }}
                      text={item.value}
                    />
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Stack gap={2}>
              <Text
                sx={{ color: "#222", fontSize: "12px", fontWeight: 700 }}
                text={t("page.calculatedSavingsSummary.title3")}
              />
              <Divider sx={{ my: 0, mb: "4px" }} />
              <Stack gap={0}>
                {[
                  {
                    label: t(
                      "page.tariff.timeline.timelineItem3.list.listItem4",
                    ),
                    value: `${flexSupplyPriceFlexElementPerKwh} ct/kWh`,
                  },
                  {
                    label: t(
                      "page.tariff.timeline.timelineItem3.list.listItem3",
                    ),
                    value: `${flexSupplyPriceFixedElementPerKwh} ct/kWh`,
                  },
                  {
                    label: t(
                      "page.tariff.timeline.timelineItem3.list.listItem2",
                    ),
                    value: `${flexWorkingPricePerKwh} ct/kWh`,
                  },
                ].map((item, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Text
                      sx={{
                        color: "#222",
                        fontSize: "11px",
                        fontWeight: 400,
                        lineHeight: 1.2,
                      }}
                      text={`${item.label}:`}
                    />
                    <Text
                      sx={{
                        color: "#222",
                        fontSize: "11px",
                        fontWeight: 400,
                        lineHeight: 2,
                      }}
                      text={item.value}
                    />
                  </Stack>
                ))}
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: "pointer", width: "100%", bgcolor: "#daecfa00" }}
              onClick={() => setIsExpanded(false)}
            >
              <Text
                sx={{
                  color: "#000",
                  fontSize: "11px",
                  fontWeight: 400,
                  textDecoration: "underline",
                  marginY: "10px",
                }}
                text={t("page.calculatedSavingsSummary.closeSummary")}
              />
              <ChevronDown style={{ transform: "rotate(180deg)" }} />
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default CalculatedSavingsSummary;
